import { PropsWithChildren, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useGetSetting } from 'api/settings'
import { settingsActions, settingsSelectors } from 'store/settings'
import { loginExternal } from 'api/auth'
import { useLogout } from 'hooks/useLogout'
import { AuthModeEnum } from 'types/setting'
import { getMyChats } from 'api/chats'

// postMessage protocol shared with the parent host (zenithar).
const FROM_PARENT = { auth: 'mimirAuth', logout: 'mimirLogout' } as const
const TO_PARENT = { ready: 'mimirAuthReady', success: 'mimirAuthSuccess', error: 'mimirAuthError' } as const

type MimirAuthPayload = {
  email: string
  token: string
  agentInputs?: Record<string, unknown> // Runtime agent inputs the host injects
}

type MimirAuthMessage = {
  type: typeof FROM_PARENT.auth
  payload: MimirAuthPayload
}

const isMimirAuthMessage = (data: unknown): data is MimirAuthMessage => {
  if (!data || typeof data !== 'object') return false
  const m = data as Partial<MimirAuthMessage>
  if (m.type !== FROM_PARENT.auth || !m.payload) return false
  const { email, token } = m.payload as { email?: unknown; token?: unknown }
  return typeof email === 'string' && !!email && typeof token === 'string' && !!token
}

/**
 * Bridges the portal with its parent host (zenithar) over postMessage.
 *
 * Mirrors `setting.authMode` into Redux, then — in `external` mode — keeps a
 * listener open for the whole session (not just until first login) so the
 * parent can re-authenticate on every widget open (refreshing a possibly stale
 * token via `POST /auth/login/external`) and trigger a logout.
 */
export const AuthBootstrapProvider = ({ children }: PropsWithChildren) => {
  const dispatch = useDispatch()
  const { setting } = useGetSetting()
  const authMode = useSelector(settingsSelectors.authMode)
  const logout = useLogout()
  // Guards against overlapping login/external calls while still allowing a
  // fresh re-auth on each widget open.
  const isAuthenticatingRef = useRef(false)

  useEffect(() => {
    if (setting?.portal?.authMode) {
      dispatch(settingsActions.setAuthMode(setting.portal.authMode))
    }
  }, [setting?.portal?.authMode, dispatch])

  useEffect(() => {
    console.debug('[Mimir]', { authMode })
    if (authMode !== AuthModeEnum.External) return

    const authenticate = async ({ email, token }: MimirAuthPayload, origin: string) => {
      if (isAuthenticatingRef.current) return
      isAuthenticatingRef.current = true
      try {
        const user = await loginExternal(email, token)

        console.debug('[Mimir] authenticated', { user, email, token })

        await getMyChats({})
        window.parent.postMessage({ type: TO_PARENT.success, payload: { userId: user._id } }, origin)
      } catch (e) {
        console.error('[AuthBootstrap] external login failed', e)
        window.parent.postMessage({ type: TO_PARENT.error, payload: { message: String(e) } }, origin)
      } finally {
        isAuthenticatingRef.current = false
      }
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === FROM_PARENT.logout) {
        logout()
        return
      }

      if (!isMimirAuthMessage(event.data)) return

      const { payload } = event.data
      // Store agent inputs when the host injects
      if (payload.agentInputs && typeof payload.agentInputs === 'object') {
        dispatch(settingsActions.setAgentInputs(payload.agentInputs))
      }
      void authenticate(payload, event.origin)
    }

    window.addEventListener('message', handleMessage)
    window.parent.postMessage({ type: TO_PARENT.ready }, '*')

    return () => window.removeEventListener('message', handleMessage)
  }, [authMode, logout])

  return <>{children}</>
}
